import * as React from "react"
import { Plus, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const tagPillClassName =
  "inline-flex h-6 shrink-0 items-center gap-1 rounded-[4px] border border-[color-mix(in_oklab,var(--brand-tropical-surf)_45%,transparent)] bg-[color-mix(in_oklab,var(--brand-tropical-surf)_10%,transparent)] pl-2 pr-1 font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]"

const tagPillLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  lineHeight: 1,
  margin: 0,
  padding: "2px 0 0",
}

function defaultParseTokens(raw: string): string[] {
  const trimmed = String(raw || "").trim()
  if (!trimmed) return []

  return Array.from(
    new Set(
      trimmed
        .split(/[,;\n]+/)
        .map((part) => part.trim())
        .filter(Boolean)
    )
  )
}

export interface TagsInputProps {
  id?: string
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  addAnotherPlaceholder?: string
  className?: string
  disabled?: boolean
  /** Normalize a single token before add (e.g. uppercase). */
  normalizeTag?: (token: string) => string
  /** Parse pasted or committed raw text into tokens. */
  parseTokens?: (raw: string) => string[]
  /** Optional formatter applied while typing in the draft input. */
  formatDraft?: (value: string) => string
  /** When true, paste with commas/newlines/spaces is handled as multiple tags. */
  parseMultiOnPaste?: boolean
}

export function TagsInput({
  id,
  value,
  onChange,
  placeholder = "Add item…",
  addAnotherPlaceholder = "Add another…",
  className,
  disabled = false,
  normalizeTag = (token) => token.trim(),
  parseTokens = defaultParseTokens,
  formatDraft,
  parseMultiOnPaste = true,
}: TagsInputProps): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [draft, setDraft] = React.useState("")

  const commitTags = React.useCallback(
    (next: string[]) => {
      onChange(next)
    },
    [onChange]
  )

  const addTokens = React.useCallback(
    (raw: string) => {
      const incoming = parseTokens(raw)
        .map((token) => normalizeTag(token))
        .filter(Boolean)
      if (!incoming.length) return

      const merged = [...value]
      for (const token of incoming) {
        if (!merged.includes(token)) merged.push(token)
      }
      commitTags(merged)
      setDraft("")
      requestAnimationFrame(() => inputRef.current?.focus())
    },
    [value, commitTags, normalizeTag, parseTokens]
  )

  const removeToken = React.useCallback(
    (token: string) => {
      commitTags(value.filter((item) => item !== token))
      requestAnimationFrame(() => inputRef.current?.focus())
    },
    [value, commitTags]
  )

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      if (draft.trim()) addTokens(draft)
      return
    }
    if (event.key === "Backspace" && !draft && value.length > 0) {
      commitTags(value.slice(0, -1))
    }
  }

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (!parseMultiOnPaste) return
    const text = event.clipboardData.getData("text")
    if (!text) return
    if (/[,\[\n;]/.test(text) || text.trim().includes(" ")) {
      event.preventDefault()
      addTokens(text)
    }
  }

  const resolvedPlaceholder = value.length === 0 ? placeholder : addAnotherPlaceholder

  return (
    <div
      data-slot="tags-input"
      className={cn(
        "flex min-h-8 w-full cursor-text flex-wrap items-center gap-1.5 rounded-[6px] border border-solid border-[#d3d3d3] bg-background px-2 py-1.5 font-[family-name:var(--font-primary)] transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
        disabled && "pointer-events-none opacity-50",
        className
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((token) => (
        <span
          key={token}
          data-tags-input-pill=""
          className={tagPillClassName}
          onClick={(event) => event.stopPropagation()}
        >
          <span data-tags-input-pill-label="" style={tagPillLabelStyle}>
            {token}
          </span>
          <span
            role="button"
            tabIndex={disabled ? -1 : 0}
            data-tags-input-pill-remove=""
            aria-disabled={disabled || undefined}
            className={cn(
              "inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm text-[color:var(--brand-tropical-surf)] transition-colors hover:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_18%,transparent)] [&_svg]:block [&_svg]:size-3",
              disabled && "pointer-events-none opacity-50"
            )}
            aria-label={`Remove ${token}`}
            onClick={() => {
              if (disabled) return
              removeToken(token)
            }}
            onKeyDown={(event) => {
              if (disabled) return
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault()
                removeToken(token)
              }
            }}
          >
            <X aria-hidden />
          </span>
        </span>
      ))}
      <div
        className="flex min-w-[8rem] flex-1 items-center gap-1"
        onClick={(event) => event.stopPropagation()}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(event) =>
            setDraft(formatDraft ? formatDraft(event.target.value) : event.target.value)
          }
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim()) addTokens(draft)
          }}
          placeholder={resolvedPlaceholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className="min-w-[5rem] flex-1 border-0 bg-transparent p-0 text-sm font-normal normal-case tracking-normal text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          data-font="primary"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Add item"
          disabled={disabled || !draft.trim()}
          onClick={() => addTokens(draft)}
        >
          <Plus className="size-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  )
}
